/**
 * Catalog API service
 * Handles catalog item CRUD operations and advanced filtering
 */

import { apiClient } from './client';
import {
  Catalog,
  CatalogCreateRequest,
  CatalogUpdateRequest,
  PaginationParams,
  PaginatedResponse,
  CatalogItemStatus,
} from '../types';
import { getConfig, createLogger } from '../config/environment';

const logger = createLogger('CatalogService');
const config = getConfig();

export class CatalogService {
  /**
   * Get all catalog items across all shops
   * GET /api/catalogs
   */
  async getAllCatalogItems(): Promise<Catalog[]> {
    try {
      logger.info('Fetching all catalog items');
      const response = await apiClient.get<Catalog[]>(config.catalogsEndpoint);

      if (response.status !== 200) {
        throw new Error('Failed to fetch catalog items');
      }

      logger.info(`Fetched ${response.data.length} catalog items`);
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch all catalog items', error);
      throw error;
    }
  }

  /**
   * Get all catalog items for a shop
   * GET /api/catalogs/shop/{shopId}
   */
  async getCatalogItems(
    shopId: string,
    params?: PaginationParams
  ): Promise<Catalog[]> {
    try {
      logger.info(`Fetching catalog items for shop: ${shopId}`);
      const response = await apiClient.get<Catalog[]>(
        `${config.catalogsEndpoint}/shop/${shopId}`,
        { params }
      );

      if (response.status !== 200) {
        throw new Error('Failed to fetch catalog items');
      }

      logger.info(`Fetched ${response.data.length} catalog items`);
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch catalog items', error);
      throw error;
    }
  }

  /**
   * Get single catalog item by ID
   * GET /api/catalogs/{catalogId}
   */
  async getCatalogItem(id: string): Promise<Catalog> {
    try {
      logger.info(`Fetching catalog item: ${id}`);
      const response = await apiClient.get<Catalog>(`${config.catalogsEndpoint}/${id}`);

      if (response.status !== 200) {
        throw new Error('Failed to fetch catalog item');
      }

      return response.data;
    } catch (error) {
      logger.error('Failed to fetch catalog item', error);
      throw error;
    }
  }

  /**
   * Get catalog items by category (all shops)
   * GET /api/catalogs/category/{category}
   */
  async getCatalogsByCategory(category: string): Promise<Catalog[]> {
    try {
      logger.info(`Fetching catalog items for category: ${category}`);
      const response = await apiClient.get<Catalog[]>(
        `${config.catalogsEndpoint}/category/${category}`
      );

      if (response.status !== 200) {
        throw new Error('Failed to fetch catalog items by category');
      }

      return response.data;
    } catch (error) {
      logger.error('Failed to fetch catalog items by category', error);
      throw error;
    }
  }

  /**
   * Get catalog items by shop and category
   * GET /api/catalogs/shop/{shopId}/category/{category}
   */
  async getCatalogsByShopAndCategory(shopId: string, category: string): Promise<Catalog[]> {
    try {
      logger.info(`Fetching catalog items for shop ${shopId} and category: ${category}`);
      const response = await apiClient.get<Catalog[]>(
        `${config.catalogsEndpoint}/shop/${shopId}/category/${category}`
      );

      if (response.status !== 200) {
        throw new Error('Failed to fetch catalog items by shop and category');
      }

      return response.data;
    } catch (error) {
      logger.error('Failed to fetch catalog items by shop and category', error);
      throw error;
    }
  }

  /**
   * Get catalog items by price range for a shop
   * GET /api/catalogs/price-range?shopId={shopId}&minPrice={minPrice}&maxPrice={maxPrice}
   */
  async getCatalogsByPriceRange(shopId: string, minPrice: number, maxPrice: number): Promise<Catalog[]> {
    try {
      logger.info(`Fetching catalog items for shop ${shopId} in price range ${minPrice}-${maxPrice}`);
      const response = await apiClient.get<Catalog[]>(
        `${config.catalogsEndpoint}/price-range`,
        {
          params: { shopId, minPrice, maxPrice },
        }
      );

      if (response.status !== 200) {
        throw new Error('Failed to fetch catalog items by price range');
      }

      return response.data;
    } catch (error) {
      logger.error('Failed to fetch catalog items by price range', error);
      throw error;
    }
  }

  /**
   * Get currently available catalog items for a shop
   * GET /api/catalogs/available/{shopId}
   */
  async getAvailableCatalogs(shopId: string): Promise<Catalog[]> {
    try {
      logger.info(`Fetching available catalog items for shop: ${shopId}`);
      const response = await apiClient.get<Catalog[]>(
        `${config.catalogsEndpoint}/available/${shopId}`
      );

      if (response.status !== 200) {
        throw new Error('Failed to fetch available catalog items');
      }

      return response.data;
    } catch (error) {
      logger.error('Failed to fetch available catalog items', error);
      throw error;
    }
  }

  /**
   * Create new catalog item
   * POST /api/catalogs
   * Image URL must be a valid Azure Blob Storage URL
   */
  async createCatalogItem(request: CatalogCreateRequest): Promise<Catalog> {
    try {
      // Validate image URL if provided
      if (request.imageUrl) {
        this.validateImageUrl(request.imageUrl);
      }

      logger.info(`Creating catalog item: ${request.name}`, { shopId: request.shopId });
      const response = await apiClient.post<Catalog>(config.catalogsEndpoint, request);

      if (response.status !== 201 && response.status !== 200) {
        throw new Error('Failed to create catalog item');
      }

      logger.info('Catalog item created successfully', { catalogId: response.data.id });
      return response.data;
    } catch (error) {
      logger.error('Failed to create catalog item', error);
      throw error;
    }
  }

  /**
   * Update existing catalog item
   * PUT /api/catalogs/{catalogId}
   */
  async updateCatalogItem(request: CatalogUpdateRequest): Promise<Catalog> {
    try {
      // Validate image URL if provided
      if (request.imageUrl) {
        this.validateImageUrl(request.imageUrl);
      }

      logger.info(`Updating catalog item: ${request.id}`);

      const { id, ...updateData } = request;
      const response = await apiClient.put<Catalog>(
        `${config.catalogsEndpoint}/${id}`,
        updateData
      );

      if (response.status !== 200) {
        throw new Error('Failed to update catalog item');
      }

      logger.info('Catalog item updated successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to update catalog item', error);
      throw error;
    }
  }

  /**
   * Update catalog item status
   * PATCH /api/catalogs/{catalogId}/status?status={status}
   */
  async updateCatalogStatus(catalogId: string, status: CatalogItemStatus): Promise<Catalog> {
    try {
      logger.info(`Updating catalog item status: ${catalogId} -> ${status}`);
      const response = await apiClient.patch<Catalog>(
        `${config.catalogsEndpoint}/${catalogId}/status`,
        {},
        { params: { status } }
      );

      if (response.status !== 200) {
        throw new Error('Failed to update catalog item status');
      }

      logger.info('Catalog item status updated successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to update catalog item status', error);
      throw error;
    }
  }

  /**
   * Delete catalog item
   * DELETE /api/catalogs/{catalogId}
   */
  async deleteCatalogItem(id: string): Promise<void> {
    try {
      logger.info(`Deleting catalog item: ${id}`);
      const response = await apiClient.delete(`${config.catalogsEndpoint}/${id}`);

      if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to delete catalog item');
      }

      logger.info('Catalog item deleted successfully', { catalogId: id });
    } catch (error) {
      logger.error('Failed to delete catalog item', error);
      throw error;
    }
  }

  /**
   * Validate image URL
   * Ensures it's a valid URL and preferably from Azure Blob Storage
   */
  private validateImageUrl(url: string): void {
    try {
      new URL(url);

      // Warning if not from Azure Blob Storage (optional)
      if (!url.includes('blob.core.windows.net')) {
        logger.warn('Image URL is not from Azure Blob Storage', url);
      }
    } catch {
      throw new Error(`Invalid image URL: ${url}`);
    }
  }

  /**
   * Check if image URL is accessible
   */
  async validateImageUrlAccessibility(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      logger.warn(`Image URL not accessible: ${url}`, error);
      return false;
    }
  }
}

// Create singleton instance
export const catalogService = new CatalogService();

